import { Entity, System } from "ecsy";
import { Magnet, Buff, Buffed, Cooldown, EffectRadius, ElementInfo, HealBooster, Health, OnBuff, TemporaryBuff, Triggered, VisibleRange, Ownership, ElectricCharge, CopyBuffs } from "../components";
import { addElementStat, getEntityPlayer } from "../composition";
import { BehaviorsConstants, CellCategory } from "../constants";
import { ElementStats, getCellCategory } from "../elementDisplayInfo";
import { getEntityById } from "../entityFactory";
import { Game } from "../game";
import { Player } from "../player";
import { selectAndFilter, triggerEffect } from "../triggeredEffects";
import { addComponent, getOrAddComponent } from "../utils";

export class BuffSystem extends System {
	private game: Game;
	private boostersByPlayer = new Map<Player, Entity[]>();

	constructor(world: any, attributes: any) {
		super(world, attributes);
		this.game = attributes.game;
	}

	init() { }

	execute(delta: number) {
		//Keep track of how many boosters each player has because we have to check it every time a buff happens
		this.boostersByPlayer.clear();
		if (this.queries.boosters.results.length > 0) {
			for (const entity of this.queries.boosters.results) {
				const player = getEntityPlayer(entity, this.game);
				if (player) {
					if (!this.boostersByPlayer.has(player)) this.boostersByPlayer.set(player, []);
					const playersList = this.boostersByPlayer.get(player)!;
					playersList.push(entity);
				}
			}
		}

		const expiredBuffs = new Array<Entity>();

		this.queries.buff.results.forEach(entity => {
			const component = entity.getComponent(Buff);
			const buffs = component.buffs;

			//Clean up exhausted buffs without invalidating loop
			if (buffs.every(buff => buff.max !== undefined && buff.max <= 0)) {
				expiredBuffs.push(entity)
				return;
			}

			const souces = new Array<number>();
			for (const buff of buffs) {
				let { stat, value, isPermanent } = buff;
				if (stat === 'health') {
					value += this.runHealBoosts(entity);
				}
				this.applyEntityBuff(entity, stat, value, false, isPermanent || false);
				if (buff.sourceEntity) {
					souces.push(buff.sourceEntity);
				}

				if (isPermanent) {
					this.addPermenentBuff(entity, stat, value);
				}

				if (buff.max) {
					--buff.max
				}
			}
			this.setBuffSources(entity, souces)
		});

		expiredBuffs.forEach(entity => entity.removeComponent(Buff));

		this.queries.temporaryBuff.added?.forEach(entity => {
			const tempBuff = entity.getComponent(TemporaryBuff);
			for (const buff of tempBuff.buffs) {
				buff.applied = false;
				if (buff.stat === 'health') {
					buff.value += this.runHealBoosts(entity); //assume these are not shared between entities!
				}
			}
		});

		this.queries.temporaryBuff.results.forEach(entity => {
			const tempBuff = entity.getComponent(TemporaryBuff);
			for (const buff of tempBuff.buffs) {
				if (buff.applied) continue;

				let { stat, value } = buff;
				this.applyEntityBuff(entity, stat, value, true, false);
				buff.applied = true;
			}
		});

		this.queries.temporaryBuff.removed?.forEach(entity => {
			const tempBuff = entity.getRemovedComponent(TemporaryBuff);
			for (const buff of tempBuff.buffs) {
				if (buff.applied) {
					this.applyEntityBuff(entity, buff.stat, -buff.value, true, false);
				}
			}
		});

		this.queries.buffTriggers.results.forEach(entity => {
			const buff = entity.getComponent(Buff);
			const trigger = entity.getComponent(OnBuff);

			if (buff.buffs.some(buff => buff.stat === trigger.stat)) {
				triggerEffect(trigger.effect, entity);
			}
		});

		this.queries.copyBuffs.added?.forEach(entity => {
			const copyBuffs = entity.getComponent(CopyBuffs);
			const sourceEntity = getEntityById(copyBuffs.sourceId);
			if (!sourceEntity || !sourceEntity.hasComponent(Buffed)) return;

			const sourceBuffed = sourceEntity.getComponent(Buffed);
			const sourceStats = Object.keys(sourceBuffed).filter(stat => !stat.startsWith("last")) as [keyof ElementStats];
			for (const stat of sourceStats) {
				const value = sourceBuffed[stat];
				if (value !== 0) {
					this.applyEntityBuff(entity, stat as keyof ElementStats, value, false, false);
				}
			}
		});
	}

	applyEntityBuff(entity: Entity, stat: keyof ElementStats, value: number, isTemporary: boolean, isPermanent: boolean) {
		if (stat === 'health' && entity.hasComponent(Health)) {
			const owner = entity.getComponent(Ownership);

			// Track Stat for achievements
			if (owner !== undefined
				&& owner.playerId !== undefined
				&& this.game.players[owner.playerId] !== undefined
			) {
				this.game.incrementRoundStat(owner.playerId, 'hpGained', value);
			}

			const health = entity.getMutableComponent(Health);
			health.hp += value;
		}
		else if (stat === 'cooldown' && entity.hasComponent(Cooldown)) {
			const cooldown = entity.getMutableComponent(Cooldown);
			if (cooldown.cooldown + value < BehaviorsConstants.MinCooldown) {
				value = -(cooldown.cooldown - BehaviorsConstants.MinCooldown);
			}
			cooldown.cooldown = cooldown.cooldown + value;
			if (cooldown.remaining > BehaviorsConstants.MinCooldown) {
				cooldown.remaining = Math.max(BehaviorsConstants.MinCooldown, cooldown.remaining + value);
			}
		}
		else if (stat === 'range' && entity.hasComponent(VisibleRange)) {
			const range = entity.getMutableComponent(VisibleRange);
			range.range += value;
		}
		else if (stat === 'effectRadius' && entity.hasComponent(EffectRadius)) {
			const effect = entity.getMutableComponent(EffectRadius);
			effect.radius += value;
		}
		else if (stat === "charge") {
			if (!entity.hasComponent(ElectricCharge)) {
				addComponent(entity, ElectricCharge, { charge: value });
			}
			else {
				const charge = entity.getMutableComponent(ElectricCharge);
				charge.charge += value;
				if (charge.charge < 0) charge.charge = 0;
			}
		}

		let buffed: Buffed;
		if (entity.hasComponent(Buffed)) {
			buffed = entity.getMutableComponent(Buffed);
		}
		else {
			buffed = getOrAddComponent(entity, Buffed);
		}
		buffed[stat] += value;
		buffed.lastBuffed = stat;
		buffed.lastBuffedValue = value;
		buffed.lastBuffWasPermenent = isPermanent;
		buffed.lastBuffWasTemporary = isTemporary;
	}

	addPermenentBuff(entity: Entity, stat: keyof ElementStats, value: number) {
		const compEl = this.game.getEntityCompElement(entity);
		if (compEl) {
			addElementStat(compEl, stat, value);
		}
	}

	runHealBoosts(entity: Entity) {
		const player = getEntityPlayer(entity, this.game);
		if (!player) return 0;
		const boosters = this.boostersByPlayer.get(player);
		if (!boosters || boosters.length === 0) return 0;

		let totalBoost = 0;
		for (const booster of boosters) {
			const boost = booster.getComponent(HealBooster);

			if (boost.categoryFilter !== CellCategory.None) {
				const info = booster.getComponent(ElementInfo);
				const passesFilter = info && info.category === boost.categoryFilter;
				if (!passesFilter) continue;
			}

			totalBoost += boost.amount

			//TODO: some signal that the booster did something. This triggered will cause a flash, good enough for now but hacky
			addComponent(booster, Triggered);
		}
		return totalBoost;
	};

	setBuffSources(entity: Entity, sources: Array<number>, buffed = entity.getComponent(Buffed)) {
		buffed.lastBuffSources = sources;
	}
}


BuffSystem.queries = {
	buff: { components: [Buff] },
	temporaryBuff: { components: [TemporaryBuff], listen: { added: true, changed: true, removed: true } },
	buffTriggers: { components: [Buff, OnBuff] },
	boosters: { components: [HealBooster, ElementInfo] },
	copyBuffs: { components: [CopyBuffs], listen: {added: true} },
}
